/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { OperationHandler } from 'src/core/services/api/createOperationCall';
import { LuisId } from 'src/luis/types/Resource';
import { PaginatedResourceStoreHouse } from 'src/luis/types/stores/core/PaginatedResourceStoreHouse';
import { ResourceStore } from 'src/luis/types/stores/core/ResourceStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

/**
 * Creates a handler to sequentially fetch pages full of certain resource.
 * The fetching (chunking) process is repeatedly
 * called until it is canceled or the returned resources
 * are empty.
 *
 * @param storeHouse The store house that contains the resources.
 * @param excludedResourceIds Resources ids to be excluded from the API fetched data.
 * @returns A handler used for the async sequential fetching.
 */
export const createChunkingAsyncHandler = <STORE extends ResourceStore, SWAGGER>({
    storeHouse,
    mapper,
    excludedResourceIds
}: {
    storeHouse: ResourceStoreHouse<STORE> | PaginatedResourceStoreHouse<STORE>;
    mapper: (swagger: SWAGGER) => STORE;
    excludedResourceIds?: LuisId[];
}): OperationHandler<SWAGGER[]> => {
    const handleCall = (swaggers: SWAGGER[]) => {
        const stores = swaggers.map(mapper);

        if (stores.length === 0) {
            storeHouse.setIsChunking(false);

            return;
        }

        let filteredStores = stores;

        if (excludedResourceIds) {
            filteredStores = stores.filter(d => !excludedResourceIds.find(id => id === d.id));
        }

        if (isPaginatedResourceStore(storeHouse)) {
            storeHouse.batchAddWithChunking(filteredStores);
        } else {
            storeHouse.batchAdd(filteredStores);
        }
    };

    const retryStrategy = () => storeHouse.isChunking;

    return { handleCall, retryStrategy, timeToDelay: () => 200 };
};

const isPaginatedResourceStore = <T extends ResourceStore>(
    storeHouse: ResourceStoreHouse<T> | PaginatedResourceStoreHouse<T>
): storeHouse is PaginatedResourceStoreHouse<T> => !!(<PaginatedResourceStoreHouse<T>>storeHouse).pages;
